import React, { useCallback, useEffect, useState } from 'react';
import type { FC } from 'react';

import type { EditorActions } from '@atlaskit/editor-core';
import type { InlineCommentViewComponentProps } from '@atlaskit/editor-plugins/annotation';

import { getEditorAnnotationEventEmitter } from '@confluence/annotation-event-emitter';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';
import type { CommentAction } from '@confluence/inline-comments-common/entry-points/inlineCommentsTypes';
import { useAnnotationsDispatch } from '@confluence/annotation-provider-store';
import { fg } from '@confluence/feature-gating';
import { useShowCommentsPanel } from '@confluence/comments-panel';
import { useCommentsPanelActions, useCommentsPanelScroll } from '@confluence/comments-panel-utils';

import { InlineComment } from '../InlineComment';

export type ViewComponentProps = {
	pageId: string;
	editorActions?: EditorActions;
};

export const editorHandleNavigationClick = (nextMarkerRef: string) => {
	const emitter = getEditorAnnotationEventEmitter();
	emitter.emit('setselectedannotation', nextMarkerRef);
};

export const ViewComponent: FC<InlineCommentViewComponentProps & ViewComponentProps> = ({
	annotations: clickedAnnotations,
	getInlineNodeTypes,
	editorActions,
	onClose,
	onDelete,
	isOpeningMediaCommentFromToolbar = false,
	isOffline,
}) => {
	const { setEditorViewProps } = useAnnotationsDispatch();
	const isLivePage = useIsCurrentPageLive();

	const supportedCommentActions: CommentAction[] = !isLivePage
		? ['edit', 'resolve']
		: ['edit', 'resolve', 'delete'];

	const { setCurrentlySelectedThreadKey } = useCommentsPanelActions();
	const { scrollCommentsPanelToThread } = useCommentsPanelScroll();
	const { isCommentsPanelShown } = useShowCommentsPanel();

	const [isInlineCommentVisible, setIsInlineCommentVisible] = useState<boolean>(false);
	const inlineCommentRef = useCallback((node: any) => {
		if (node === null) {
			setIsInlineCommentVisible(false);
		} else {
			setIsInlineCommentVisible(true);
		}
	}, []);

	const handleResolve = () => {
		setEditorViewProps(null);
	};

	const handleDelete = useCallback(
		(id: string) => {
			setEditorViewProps(null);
			onDelete && onDelete(id);
		},
		[setEditorViewProps, onDelete],
	);

	useEffect(() => {
		const annotationId = clickedAnnotations[0]?.id;
		if (!annotationId) return;

		const shouldScrollCommentsPanelToComment =
			isCommentsPanelShown && clickedAnnotations.length > 0;
		if (shouldScrollCommentsPanelToComment && fg('confluence-frontend-comments-panel')) {
			setCurrentlySelectedThreadKey(annotationId);
			scrollCommentsPanelToThread({ annotationId, annotationClicked: true });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [clickedAnnotations[0]?.id]);

	const shouldRenderStandaloneInlineComment =
		clickedAnnotations.length > 0 &&
		(!fg('confluence-frontend-comments-panel') || !isCommentsPanelShown);
	return shouldRenderStandaloneInlineComment ? (
		<InlineComment
			isEditor
			annotation={clickedAnnotations[0]}
			getInlineNodeTypes={getInlineNodeTypes}
			onClose={onClose}
			editorActions={editorActions}
			onNavigationClick={(nextMarkerRef: string) => editorHandleNavigationClick(nextMarkerRef)}
			onResolve={handleResolve}
			onDelete={handleDelete}
			supportedTopLevelActions={supportedCommentActions}
			isOpeningMediaCommentFromToolbar={isOpeningMediaCommentFromToolbar}
			inlineCommentRef={inlineCommentRef}
			isInlineCommentVisible={isInlineCommentVisible}
			isOffline={isOffline}
			{...(fg('confluence_frontend_inline_comment_switch') && {
				key: `inline-comment-${clickedAnnotations[0]?.id}`,
			})}
		/>
	) : (
		<></>
	);
};
